/**
 * Les dates de la semaine, cote regie. De l'arithmetique de calendrier en UTC, sur des textes AAAA-MM-JJ : aucun
 * calcul ne lit le fuseau de la machine (`getDay`, `getHours`, `new Date(annee, mois, jour)`), parce que celle de
 * Nicolas est a l'heure de Paris et qu'un test ou une autre machine ne l'est pas. Seule la question « quel jour
 * est-on a Paris ? » passe par `Intl`.
 *
 * Ce sont les regles de `heures.ts` du backend (`lundiDe`, `lundiSuivant`) : la regie et le serveur doivent
 * s'accorder sur la semaine que `GET /api/diffusion/semaine` rend sans `lundi` (la suivante) et sur celle qu'un lien
 * « Cette semaine » demande.
 */

const MS_PAR_JOUR = 86_400_000;

/** Un jour : AAAA-MM-JJ, en chiffres ASCII (le `\d` de JavaScript n'en connait pas d'autres). */
const JOUR = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Minuit UTC d'un jour AAAA-MM-JJ, ou `null` si ce n'est pas une vraie date du calendrier. `setUTCFullYear` et non
 * `Date.UTC` : celui-ci lit une annee de 0 a 99 comme 1900 a 1999.
 */
function minuitUtc(jour: string): number | null {
  if (!JOUR.test(jour)) {
    return null;
  }
  const date = new Date(0);
  date.setUTCFullYear(
    Number(jour.slice(0, 4)),
    Number(jour.slice(5, 7)) - 1,
    Number(jour.slice(8, 10)),
  );
  // Un 30 fevrier ou un mois 13 debordent sur le mois d'apres : la date relue n'est plus celle qu'on a ecrite.
  return date.toISOString().slice(0, 10) === jour ? date.getTime() : null;
}

/** Le jour ISO d'un minuit UTC : 1 pour le lundi, 7 pour le dimanche. */
const jourIso = (minuit: number): number => ((new Date(minuit).getUTCDay() + 6) % 7) + 1;

/** Un minuit UTC ecrit AAAA-MM-JJ. */
const ecrireJour = (minuit: number): string => new Date(minuit).toISOString().slice(0, 10);

/**
 * Vrai pour un texte qui est une date AAAA-MM-JJ du calendrier et qui tombe un lundi, et pour rien d'autre. La
 * valeur vient de l'adresse (`?lundi=`), donc de n'importe qui : un nombre, un 30 fevrier, un mardi, une date avec
 * une heure ne passent pas.
 */
export function estUnLundi(valeur: unknown): valeur is string {
  if (typeof valeur !== "string") {
    return false;
  }
  const minuit = minuitUtc(valeur);
  return minuit !== null && jourIso(minuit) === 1;
}

/**
 * La date `jour` (AAAA-MM-JJ) decalee de `n` jours de calendrier, vers l'avant ou l'arriere. Un jour du calendrier,
 * pas une duree de 24 h : le dimanche d'un changement d'heure est un jour comme un autre. `jour` doit etre une vraie
 * date ; les appelants passent des dates que le serveur ou `estUnLundi` ont deja jugees.
 */
export function ajouterJours(jour: string, n: number): string {
  const minuit = minuitUtc(jour);
  if (minuit === null) {
    throw new RangeError("La date n'est pas une date AAAA-MM-JJ du calendrier.");
  }
  return ecrireJour(minuit + n * MS_PAR_JOUR);
}

/**
 * Midi UTC d'un jour AAAA-MM-JJ, ou `null` si ce n'est pas une vraie date du calendrier. Midi, pour qu'un format lu
 * dans un fuseau qui n'est pas UTC ne change pas le jour ; les formats de la regie lisent pourtant ce jour en UTC.
 */
export function midiUtc(jour: string): Date | null {
  const minuit = minuitUtc(jour);
  return minuit === null ? null : new Date(minuit + MS_PAR_JOUR / 2);
}

/** Les sept jours (AAAA-MM-JJ) de la semaine d'un lundi, du lundi au dimanche. */
export function joursDeLaSemaine(lundi: string): string[] {
  return Array.from({ length: 7 }, (_, rang) => ajouterJours(lundi, rang));
}

/** Les parties de la date de Paris : `en-CA` n'est qu'un moyen d'obtenir des chiffres ASCII, on lit les parties. */
const DATE_DE_PARIS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Le jour (AAAA-MM-JJ) qu'il est a Paris a l'instant `d`. C'est la date de Paris qui compte, pas sa date UTC : le
 * dimanche 18 octobre 2026 a 22:30 UTC est deja le lundi 19 a 00:30 a Paris.
 */
export function jourParis(d: Date): string {
  const parties = DATE_DE_PARIS.formatToParts(d);
  const lire = (type: Intl.DateTimeFormatPartTypes): string =>
    parties.find((partie) => partie.type === type)?.value ?? "";
  return `${lire("year").padStart(4, "0")}-${lire("month")}-${lire("day")}`;
}

/** Minuit UTC du lundi de la semaine du jour de Paris de `d`. */
function lundiEnMs(d: Date): number {
  const minuit = minuitUtc(jourParis(d));
  if (minuit === null) {
    throw new RangeError("L'instant n'est pas une date valide.");
  }
  return minuit - (jourIso(minuit) - 1) * MS_PAR_JOUR;
}

/** Le lundi (AAAA-MM-JJ, heure de Paris) de la semaine de `d` : la semaine ISO, du lundi au dimanche. */
export function lundiCourant(d: Date): string {
  return ecrireJour(lundiEnMs(d));
}

/** Le lundi (AAAA-MM-JJ, heure de Paris) de la semaine d'apres celle de `d`. */
export function lundiSuivant(d: Date): string {
  return ecrireJour(lundiEnMs(d) + 7 * MS_PAR_JOUR);
}

/** Un jour et une heure de Paris reconstruits depuis un instant : `en-CA` et `h23` donnent des chiffres ASCII sans « 24 h ». */
const HORLOGE_DE_PARIS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** Le jour et l'heure de Paris a l'instant `ms`, lus comme s'ils etaient en UTC (pour en tirer l'ecart de Paris). */
function parisCommeUtc(ms: number): { jour: string; heure: string; utc: number } {
  const parties = HORLOGE_DE_PARIS.formatToParts(new Date(ms));
  const lire = (type: Intl.DateTimeFormatPartTypes): string =>
    parties.find((partie) => partie.type === type)?.value ?? "";
  const jour = `${lire("year").padStart(4, "0")}-${lire("month")}-${lire("day")}`;
  const heure = `${lire("hour")}:${lire("minute")}`;
  const utc = Date.UTC(
    Number(lire("year")),
    Number(lire("month")) - 1,
    Number(lire("day")),
    Number(lire("hour")),
    Number(lire("minute")),
  );
  return { jour, heure, utc };
}

/**
 * L'instant (ISO 8601 en UTC) de `jour` (AAAA-MM-JJ) a `heure` (HH:MM) a Paris, ou `null` si l'un des deux n'est pas
 * valable, ou si cette heure n'existe pas a Paris (la nuit du passage a l'heure d'ete saute de 02:00 a 03:00). Aucun
 * calcul ne lit le fuseau de la machine : l'ecart de Paris est lu par `Intl`, deux fois pour tenir le changement d'heure.
 */
export function instantParis(jour: string, heure: string): string | null {
  if (minuitUtc(jour) === null || !/^([01]\d|2[0-3]):[0-5]\d$/.test(heure)) {
    return null;
  }
  const voulu = Date.parse(`${jour}T${heure}:00Z`);
  if (Number.isNaN(voulu)) {
    return null;
  }
  let instant = voulu;
  for (let essai = 0; essai < 2; essai += 1) {
    instant = voulu - (parisCommeUtc(instant).utc - instant);
  }
  const relu = parisCommeUtc(instant);
  return relu.jour === jour && relu.heure === heure ? new Date(instant).toISOString() : null;
}
